import { TABLE_LENGTH_M, TABLE_WIDTH_M } from "../../utils/tableUv";

const COLOR_A = "#22d3ee";
const COLOR_B = "#fb7185";
const U0 = -0.42;
const U1 = 1.42;
const V0 = -0.08;
const V1 = 1.08;
const DEPTH_BINS = 4;
const LAT_BINS = 6;

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function xy(u, v) {
  return {
    x: (u - U0) * TABLE_LENGTH_M * 100,
    y: (v - V0) * TABLE_WIDTH_M * 100,
  };
}

const VB = {
  w: (U1 - U0) * TABLE_LENGTH_M * 100,
  h: (V1 - V0) * TABLE_WIDTH_M * 100,
};

function tableRect() {
  const a = xy(0, 0);
  const b = xy(1, 1);
  return { x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y };
}

function standRect(player) {
  if (player === "B") {
    const a = xy(1, 0);
    const b = xy(U1, 1);
    return { x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y };
  }
  const a = xy(U0, 0);
  const b = xy(0, 1);
  return { x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y };
}

function binCells(points, player) {
  const mine = points.filter((p) => p.player === player && num(p.u) != null && num(p.v) != null);
  if (!mine.length) return [];
  const uMin = player === "B" ? 0.88 : U0;
  const uMax = player === "B" ? U1 : 0.12;
  const cells = [];
  let max = 1;
  for (let i = 0; i < DEPTH_BINS; i += 1) {
    for (let j = 0; j < LAT_BINS; j += 1) {
      const u0 = uMin + ((uMax - uMin) * i) / DEPTH_BINS;
      const u1 = uMin + ((uMax - uMin) * (i + 1)) / DEPTH_BINS;
      const v0 = (j) / LAT_BINS;
      const v1 = (j + 1) / LAT_BINS;
      const count = mine.filter((p) => p.u >= u0 && p.u < u1 && p.v >= v0 && p.v < v1).length;
      if (count > max) max = count;
      if (count > 0) cells.push({ u0, u1, v0, v1, count });
    }
  }
  return cells.map((c) => ({ ...c, frac: c.count / max }));
}

/**
 * Top-down table heat of serve hip positions (Player A left end, B right).
 */
export function ServeHeatMap({
  points = [],
  selectedIndex = null,
  onSelectIndex = null,
  labelA = "A",
  labelB = "B",
  ariaLabel = "Serve hip heat map",
}) {
  const list = Array.isArray(points) ? points : [];
  const tbl = tableRect();
  const standA = standRect("A");
  const standB = standRect("B");
  const net = xy(0.5, 0);
  const netB = xy(0.5, 1);
  const binsA = binCells(list, "A");
  const binsB = binCells(list, "B");
  const selectable = typeof onSelectIndex === "function";

  return (
    <div className="serve-heat-map">
      <svg
        className="serve-heat-map__svg"
        viewBox={`0 0 ${VB.w.toFixed(1)} ${VB.h.toFixed(1)}`}
        role="img"
        aria-label={ariaLabel}
      >
        <rect className="serve-heat-map__floor" x="0" y="0" width={VB.w} height={VB.h} rx="6" />
        <rect
          className="serve-heat-map__stand serve-heat-map__stand--a"
          x={standA.x}
          y={standA.y}
          width={standA.w}
          height={standA.h}
        />
        <rect
          className="serve-heat-map__stand serve-heat-map__stand--b"
          x={standB.x}
          y={standB.y}
          width={standB.w}
          height={standB.h}
        />
        <rect
          className="serve-heat-map__table"
          x={tbl.x}
          y={tbl.y}
          width={tbl.w}
          height={tbl.h}
          rx="2"
        />
        <line
          className="serve-heat-map__net"
          x1={net.x}
          y1={net.y}
          x2={netB.x}
          y2={netB.y}
        />
        {binsA.map((c, i) => {
          const a = xy(c.u0, c.v0);
          const b = xy(c.u1, c.v1);
          return (
            <rect
              key={`a-${i}`}
              className="serve-heat-map__bin serve-heat-map__bin--a"
              x={Math.min(a.x, b.x)}
              y={Math.min(a.y, b.y)}
              width={Math.abs(b.x - a.x)}
              height={Math.abs(b.y - a.y)}
              opacity={0.12 + c.frac * 0.45}
            />
          );
        })}
        {binsB.map((c, i) => {
          const a = xy(c.u0, c.v0);
          const b = xy(c.u1, c.v1);
          return (
            <rect
              key={`b-${i}`}
              className="serve-heat-map__bin serve-heat-map__bin--b"
              x={Math.min(a.x, b.x)}
              y={Math.min(a.y, b.y)}
              width={Math.abs(b.x - a.x)}
              height={Math.abs(b.y - a.y)}
              opacity={0.12 + c.frac * 0.45}
            />
          );
        })}
        {list.map((p, i) => {
          if (num(p.u) == null || num(p.v) == null) return null;
          const pt = xy(p.u, p.v);
          const isB = p.player === "B";
          const idx = Number(p.index);
          const selected = Number.isFinite(selectedIndex) && idx === selectedIndex;
          return (
            <g
              key={`p-${p.player}-${idx || i}`}
              className={`serve-heat-map__dot ${isB ? "is-b" : "is-a"} ${
                selected ? "is-selected" : ""
              }`}
              transform={`translate(${pt.x} ${pt.y})`}
            >
              {selectable ? (
                <circle
                  r={selected ? 7.2 : 5}
                  role="button"
                  tabIndex={0}
                  aria-label={`${isB ? labelB : labelA} ${idx || i + 1}, ${
                    num(p.time_to_ready_ms) != null && !p.ready_timeout
                      ? `${Math.round(p.time_to_ready_ms)} ms`
                      : "—"
                  }`}
                  onClick={() => onSelectIndex(idx)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" || ev.key === " ") {
                      ev.preventDefault();
                      onSelectIndex(idx);
                    }
                  }}
                />
              ) : (
                <circle r={selected ? 7.2 : 5} />
              )}
              {num(p.time_to_ready_ms) != null && !p.ready_timeout ? (
                <text
                  className="serve-heat-map__dot-ms"
                  x={0}
                  y={selected ? -11 : -9}
                  textAnchor="middle"
                >
                  {Math.round(p.time_to_ready_ms)}
                </text>
              ) : null}
            </g>
          );
        })}
        <text
          className="serve-heat-map__end-label serve-heat-map__end-label--a"
          x={xy(-0.2, 0.5).x}
          y={xy(-0.2, 0.5).y}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {labelA}
        </text>
        <text
          className="serve-heat-map__end-label serve-heat-map__end-label--b"
          x={xy(1.2, 0.5).x}
          y={xy(1.2, 0.5).y}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {labelB}
        </text>
      </svg>
    </div>
  );
}

export default ServeHeatMap;
